import { Observable } from "rxjs";

import {
  CHUNKED_UPLOAD_MIN_SIZE,
  ensureUploadConfig,
  UPLOAD_MAX_IN_FLIGHT_INSTANCES
} from "@/js/upload/config";
import { splitFilesForBatch, splitFilesIntoBatches } from "@/js/upload/batch";
import {
  shouldUseBatchUploadForStack,
  shouldUseChunkedUploadForStack
} from "@/js/upload/strategy";
import { uploadFileChunked } from "@/js/upload/chunked";
import {
  finalizeChunkedUpload,
  getUploadAvailability,
  uploadFileBatch,
  uploadFileSimple
} from "@/js/upload/api";
import {
  buildProcessingPayload,
  emitProcessingSnapshot,
  notifyProcessingStatus,
  waitForUploadCompletion
} from "@/js/upload/status";

const buildAvailabilityErrorMessage = availability => {
  if (!availability) {
    return "Uploads are temporarily unavailable. Please try again.";
  }
  const base =
    availability.error || "Uploads are temporarily unavailable. Please try again.";
  const uploadLimit = Number(availability.upload_limit);
  const uploadActive = Number(availability.active_uploads);
  if (
    Number.isFinite(uploadLimit) &&
    uploadLimit > 0 &&
    Number.isFinite(uploadActive)
  ) {
    return `${base} (active ${uploadActive} / limit ${uploadLimit}).`;
  }
  const ingestLimit = Number(availability.ingest_limit);
  const ingestActive = Number(availability.active_ingests);
  if (
    Number.isFinite(ingestLimit) &&
    ingestLimit > 0 &&
    Number.isFinite(ingestActive)
  ) {
    return `${base} (active ${ingestActive} / limit ${ingestLimit}).`;
  }
  return base;
};

const ensureUploadAvailability = async () => {
  const availability = await getUploadAvailability();
  if (availability && availability.allowed === false) {
    const message = buildAvailabilityErrorMessage(availability);
    const error = new Error(message);
    error.details = availability;
    throw error;
  }
};

// High-level upload orchestration for DICOM series.
const sendDicomFilesViaChunkedUpload = (dbSeriesId, stack, options = {}) => {
  const uploadOptions = { resume: true, ...options };
  let sendCounter = 0;

  return new Observable(sub => {
    (async () => {
      const seriesTotalFiles = stack.isMultiframe
        ? 1
        : Array.isArray(stack.imageIds)
        ? stack.imageIds.length
        : 0;
      // Track completed ingests so processing progress reflects the whole series.
      let processedBase = 0;
      const onStatus = status => {
        const processing = buildProcessingPayload(status, {
          processedOffset: processedBase,
          seriesTotalFiles
        });
        if (processing) {
          sub.next({ processing });
        }
      };
      const onUpdate = async () => {
        sendCounter++;
        if (
          stack.isMultiframe
            ? sendCounter == 1
            : stack.imageIds.length == sendCounter
        ) {
          sub.complete();
        }
      };

      const bytesByInstance = {};
      const seriesTotalBytes = stack.isMultiframe
        ? stack.dataSet?.byteArray?.length || 0
        : stack.imageIds.reduce((total, key) => {
            const instance = stack.instances[key];
            return total + (instance?.file?.size || 0);
          }, 0);
      const reportSeriesProgress = () => {
        const bytesSent = Object.values(bytesByInstance).reduce(
          (sum, value) => sum + value,
          0
        );
        sub.next({ bytesSent, bytesTotal: seriesTotalBytes });
      };

      if (stack.isMultiframe) {
        const blob = new Blob([stack.dataSet.byteArray], {
          type: "application/dicom"
        });
        const file = new File([blob], `${stack.uniqueUID}.dcm`, {
          type: "application/dicom",
          lastModified: new Date()
        });
        bytesByInstance[stack.uniqueUID] = 0;
        await uploadFileChunked(dbSeriesId, stack.uniqueUID, file, {
          ...uploadOptions,
          onStatus,
          onProgress: ({ bytesSent }) => {
            bytesByInstance[stack.uniqueUID] = bytesSent;
            reportSeriesProgress();
          }
        });
        sub.next({});
        processedBase += 1;
        emitProcessingSnapshot(sub, processedBase, seriesTotalFiles);
        onUpdate();
      } else {
        for (const key of stack.imageIds) {
          const instance = stack.instances[key];
          if (!instance || !instance.file) {
            throw new Error("Missing DICOM file for upload");
          }
          const instanceUid = instance.metadata.instanceUID;
          bytesByInstance[instanceUid] = 0;
          await uploadFileChunked(dbSeriesId, instanceUid, instance.file, {
            ...uploadOptions,
            onStatus,
            onProgress: ({ bytesSent }) => {
              bytesByInstance[instanceUid] = bytesSent;
              reportSeriesProgress();
            }
          });
          sub.next({});
          processedBase += 1;
          emitProcessingSnapshot(sub, processedBase, seriesTotalFiles);
          onUpdate();
        }
      }
    })().catch(err => sub.error(err));
  });
};

const sendDicomFilesViaSimpleUpload = (dbSeriesId, stack, options = {}) => {
  let sendCounter = 0;

  return new Observable(sub => {
    (async () => {
      const seriesTotalFiles = stack.isMultiframe
        ? 1
        : Array.isArray(stack.imageIds)
        ? stack.imageIds.length
        : 0;
      // Track completed ingests so processing progress reflects the whole series.
      let processedBase = 0;
      const onStatus = status => {
        const processing = buildProcessingPayload(status, {
          processedOffset: processedBase,
          seriesTotalFiles
        });
        if (processing) {
          sub.next({ processing });
        }
      };
      const onUpdate = async () => {
        sendCounter++;
        if (
          stack.isMultiframe
            ? sendCounter == 1
            : stack.imageIds.length == sendCounter
        ) {
          sub.complete();
        }
      };

      const bytesByInstance = {};
      const seriesTotalBytes = stack.isMultiframe
        ? stack.dataSet?.byteArray?.length || 0
        : stack.imageIds.reduce((total, key) => {
            const instance = stack.instances[key];
            return total + (instance?.file?.size || 0);
          }, 0);
      const reportSeriesProgress = () => {
        const bytesSent = Object.values(bytesByInstance).reduce(
          (sum, value) => sum + value,
          0
        );
        sub.next({ bytesSent, bytesTotal: seriesTotalBytes });
      };

      if (stack.isMultiframe) {
        const blob = new Blob([stack.dataSet.byteArray], {
          type: "application/dicom"
        });
        const file = new File([blob], `${stack.uniqueUID}.dcm`, {
          type: "application/dicom",
          lastModified: new Date()
        });
        bytesByInstance[stack.uniqueUID] = 0;
        const simpleResponse = await uploadFileSimple(dbSeriesId, file, {
          ...options,
          onProgress: ({ bytesSent }) => {
            bytesByInstance[stack.uniqueUID] = bytesSent;
            reportSeriesProgress();
          }
        });
        if (simpleResponse && simpleResponse.upload_id) {
          await notifyProcessingStatus(simpleResponse.upload_id, onStatus);
          await waitForUploadCompletion(simpleResponse.upload_id, {
            retryFinalize: () =>
              finalizeChunkedUpload(simpleResponse.upload_id),
            throttled: simpleResponse._throttled,
            onStatus,
            useSse: false
          });
        }
        sub.next({});
        processedBase += 1;
        emitProcessingSnapshot(sub, processedBase, seriesTotalFiles);
        onUpdate();
      } else {
        const maxInFlight = Math.max(1, UPLOAD_MAX_IN_FLIGHT_INSTANCES);
        const pending = [...stack.imageIds];
        let inFlight = 0;
        let nextIndex = 0;

        await new Promise((resolve, reject) => {
          // Simple uploads are sequential by default; can be parallelized via env.
          const startNext = () => {
            while (inFlight < maxInFlight && nextIndex < pending.length) {
              const key = pending[nextIndex++];
              const instance = stack.instances[key];
              if (!instance || !instance.file) {
                reject(new Error("Missing DICOM file for upload"));
                return;
              }

              const instanceUid = instance.metadata.instanceUID;
              bytesByInstance[instanceUid] = 0;
              inFlight += 1;

              uploadFileSimple(dbSeriesId, instance.file, {
                ...options,
                onProgress: ({ bytesSent }) => {
                  bytesByInstance[instanceUid] = bytesSent;
                  reportSeriesProgress();
                }
              })
                .then(simpleResponse => {
                  if (simpleResponse && simpleResponse.upload_id) {
                    return notifyProcessingStatus(
                      simpleResponse.upload_id,
                      onStatus
                    ).then(() =>
                      waitForUploadCompletion(simpleResponse.upload_id, {
                        retryFinalize: () =>
                          finalizeChunkedUpload(simpleResponse.upload_id),
                        throttled: simpleResponse._throttled,
                        onStatus,
                        useSse: false
                      })
                    );
                  }
                  return null;
                })
                .then(() => {
                  sub.next({});
                  processedBase += 1;
                  emitProcessingSnapshot(sub, processedBase, seriesTotalFiles);
                  onUpdate();
                })
                .then(() => {
                  inFlight -= 1;
                  if (nextIndex >= pending.length && inFlight === 0) {
                    resolve();
                    return;
                  }
                  startNext();
                })
                .catch(error => {
                  reject(error);
                });
            }
          };

          startNext();
        });
      }
    })().catch(err => sub.error(err));
  });
};

const sendDicomFilesViaBatchUpload = (dbSeriesId, stack, options = {}) => {
  return new Observable(sub => {
    (async () => {
      const seriesTotalFiles = stack.isMultiframe
        ? 1
        : Array.isArray(stack.imageIds)
        ? stack.imageIds.length
        : 0;
      // Track completed ingests so processing progress reflects the whole series.
      let processedBase = 0;
      const onStatus = status => {
        const processing = buildProcessingPayload(status, {
          processedOffset: processedBase,
          seriesTotalFiles
        });
        if (processing) {
          sub.next({ processing });
        }
      };
      if (stack.isMultiframe) {
        // Multiframe is a single file; batch adds no benefit here.
        const blob = new Blob([stack.dataSet.byteArray], {
          type: "application/dicom"
        });
        const file = new File([blob], `${stack.uniqueUID}.dcm`, {
          type: "application/dicom",
          lastModified: new Date()
        });
        const simpleResponse = await uploadFileSimple(dbSeriesId, file, {
          ...options,
          onProgress: progress => sub.next(progress)
        });
        if (simpleResponse && simpleResponse.upload_id) {
          await notifyProcessingStatus(simpleResponse.upload_id, onStatus);
          await waitForUploadCompletion(simpleResponse.upload_id, {
            throttled: simpleResponse._throttled,
            onStatus,
            useSse: false
          });
        }
        sub.next({});
        processedBase += 1;
        emitProcessingSnapshot(sub, processedBase, seriesTotalFiles);
        sub.complete();
        return;
      }

      const files = [];
      for (const key of stack.imageIds || []) {
        const instance = stack.instances[key];
        if (!instance || !instance.file) {
          throw new Error("Missing DICOM file for upload");
        }
        files.push(instance.file);
      }
      if (!files.length) {
        throw new Error("No DICOM files for batch upload");
      }

      const totalBytes = files.reduce(
        (sum, file) => sum + (file?.size || 0),
        0
      );

      const { smallFiles, largeFiles } = splitFilesForBatch(files);
      const batches = splitFilesIntoBatches(smallFiles);

      // Progress accumulators assume sequential batch uploads; if you ever
      // parallelize batches, these shared counters will need per-batch isolation.
      let batchBytesSent = 0;
      const chunkedBytesByName = {};
      const reportProgress = () => {
        const chunkedBytes = Object.values(chunkedBytesByName).reduce(
          (sum, value) => sum + value,
          0
        );
        sub.next({
          bytesSent: batchBytesSent + chunkedBytes,
          bytesTotal: totalBytes
        });
      };

      let batchBytesBase = 0;
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex += 1) {
        const batch = batches[batchIndex];
        const batchSize = batch.reduce(
          (sum, file) => sum + (file?.size || 0),
          0
        );
        sub.next({
          batchIndex: batchIndex + 1,
          batchTotal: batches.length
        });
        const onProgress = ({ bytesSent }) => {
          batchBytesSent = batchBytesBase + bytesSent;
          reportProgress();
        };
        const batchResponse = await uploadFileBatch(dbSeriesId, batch, {
          ...options,
          onProgress
        });
        if (batchResponse && batchResponse.upload_id) {
          await notifyProcessingStatus(batchResponse.upload_id, onStatus);
          await waitForUploadCompletion(batchResponse.upload_id, {
            retryFinalize: () => finalizeChunkedUpload(batchResponse.upload_id),
            throttled: batchResponse._throttled,
            onStatus
          });
        }
        batchBytesBase += batchSize;
        batchBytesSent = batchBytesBase;
        reportProgress();
        processedBase += batch.length;
        emitProcessingSnapshot(sub, processedBase, seriesTotalFiles);
      }

      // Large files avoid batch; prefer chunked when allowed, fallback to simple.
      for (const file of largeFiles) {
        const fileKey = file?.name || `${file?.size || 0}`;
        chunkedBytesByName[fileKey] = 0;
        if (file.size >= CHUNKED_UPLOAD_MIN_SIZE) {
          await uploadFileChunked(dbSeriesId, null, file, {
            ...options,
            onStatus,
            onProgress: ({ bytesSent }) => {
              chunkedBytesByName[fileKey] = bytesSent;
              reportProgress();
            }
          });
        } else {
          const simpleResponse = await uploadFileSimple(dbSeriesId, file, {
            ...options,
            onProgress: ({ bytesSent }) => {
              chunkedBytesByName[fileKey] = bytesSent;
              reportProgress();
            }
          });
          if (simpleResponse && simpleResponse.upload_id) {
            await notifyProcessingStatus(simpleResponse.upload_id, onStatus);
            await waitForUploadCompletion(simpleResponse.upload_id, {
              retryFinalize: () =>
                finalizeChunkedUpload(simpleResponse.upload_id),
              throttled: simpleResponse._throttled,
              onStatus,
              useSse: false
            });
          }
        }
        reportProgress();
        processedBase += 1;
        emitProcessingSnapshot(sub, processedBase, seriesTotalFiles);
      }

      sub.next({});
      sub.complete();
    })().catch(err => sub.error(err));
  });
};

const sendDicomFiles = (dbSeriesId, stack, options = {}) => {
  return new Observable(sub => {
    let innerSub = null;
    let cancelled = false;

    (async () => {
      // Ensure backend upload limits are loaded before choosing batch vs chunked.
      await ensureUploadConfig();
      await ensureUploadAvailability();
      let stream = null;
      if (shouldUseChunkedUploadForStack(stack, options)) {
        stream = sendDicomFilesViaChunkedUpload(dbSeriesId, stack, options);
      } else if (shouldUseBatchUploadForStack(stack, options)) {
        stream = sendDicomFilesViaBatchUpload(dbSeriesId, stack, options);
      } else {
        stream = sendDicomFilesViaSimpleUpload(dbSeriesId, stack, options);
      }

      if (cancelled) {
        return;
      }
      innerSub = stream.subscribe({
        next: value => sub.next(value),
        error: error => sub.error(error),
        complete: () => sub.complete()
      });
    })().catch(err => {
      if (!cancelled) {
        sub.error(err);
      }
    });

    return () => {
      cancelled = true;
      if (innerSub) {
        innerSub.unsubscribe();
      }
    };
  });
};

export { sendDicomFiles };
